import { useCallback, useEffect, useRef, useState } from 'react';
import type NetworkTest from 'opentok-network-test-js';
import type OT from '@opentok/client';
import { type QualityTestResults } from 'opentok-network-test-js';
import { singletonHook } from 'react-singleton-hook';
import { useDocumentVisibility } from '@doxyme/ui-foundation/hooks';
import { getLogger } from '../../libs/logger';
import { useLazyCreatePreCallTestSessionQuery } from '../../services/sessionApi.service';
import { useDevices } from '../useDevices';
import { useLocalMedia } from '../useLocalMedia';
import { useIsTouchNonHybrid } from '../../components/call/screenshareZoom/useIsTouchNonHybrid';
import { getGrowthBookConfigLoader } from '@doxyme/config';
import { useAppDispatch, useAppSelector } from '../useReduxToolkit';
import { updateRealtimeState } from '../../features/realtime/realtime.slice';
import { getIsCheckedIn } from '../../features/realtime/realtime.selectors';
import type { ConnectionTestError, ConnectionTestResult } from './types';
import { parseVideoQuality } from './utils';

let opentokClientPromise: Promise<typeof OT> | null = null;
const getOpentokClient = () => {
  if (!opentokClientPromise) {
    opentokClientPromise = import('@opentok/client').then((OT) => OT.default);
  }
  return opentokClientPromise;
};

let networkTestPromise: Promise<typeof NetworkTest> | null = null;
const getNetworkTest = () => {
  if (!networkTestPromise) {
    networkTestPromise = import('opentok-network-test-js').then(
      (mod) => mod.default,
    );
  }
  return networkTestPromise;
};

const logger = getLogger('useConnectionTest');

const hookInitValues = {
  startConnectionTest: () => {},
  stopConnectionTest: () => {},
  resetConnectionTest: () => {},
  isConnectionTestRunning: false,
  connectionTestResult: undefined as ConnectionTestResult | undefined,
  connectionTestError: undefined as ConnectionTestError | undefined,
  connectionTestRawResult: undefined as QualityTestResults | undefined,
};

export const useConnectionTestImpl = () => {
  const dispatch = useAppDispatch();

  const isCheckedIn = useAppSelector(getIsCheckedIn);

  const [createPreCallTestSession] = useLazyCreatePreCallTestSessionQuery();
  const networkTest = useRef<NetworkTest | undefined>(undefined);

  const [isConnectionTestRunning, setIsConnectionTestRunning] = useState(false);
  const [connectionTestError, setConnectionTestError] = useState<
    ConnectionTestError | undefined
  >(undefined);
  const [connectionTestResult, setConnectionTestResult] = useState<
    ConnectionTestResult | undefined
  >(undefined);
  const [connectionTestRawResult, setConnectionTestRawResult] = useState<
    QualityTestResults | undefined
  >(undefined);

  const isMobile = useIsTouchNonHybrid();
  const [isVisible, setIsVisible] = useState(true);

  const { hasAudioAccess, hasVideoAccess } = useLocalMedia();
  const { selectedMicrophoneId, selectedCameraId } = useDevices();

  const startConnectionTest = useCallback(async () => {
    const isEnabled = getGrowthBookConfigLoader().getOptional(
      'feat_prod_2384_check_in_v3_connection_test',
    );

    if (
      !isEnabled ||
      networkTest.current ||
      connectionTestError ||
      connectionTestResult ||
      typeof window === 'undefined' // SSR guard
    ) {
      return;
    }

    if (hasAudioAccess === false || hasVideoAccess === false) {
      logger.warn('MEDIA_DISABLED');
      setConnectionTestError('MEDIA_DISABLED');

      return;
    }

    if (!selectedCameraId || !selectedMicrophoneId) {
      logger.warn('DEVICE_NOT_SELECTED', {
        camera: selectedCameraId || 'undefined',
        microphone: selectedMicrophoneId || 'undefined',
      });
      setConnectionTestError('DEVICE_NOT_SELECTED');

      return;
    }

    const { data: sessionData, error: createSessionError } =
      await createPreCallTestSession();

    if (!sessionData) {
      logger.warn('CREATE_SESSION_ERROR', createSessionError);
      setConnectionTestError('CREATE_SESSION_ERROR');

      return;
    }

    const videoSource = selectedCameraId || undefined;
    const audioSource = selectedMicrophoneId || undefined;

    logger.debug('network test created', {
      videoSource,
      audioSource,
    });

    // @TODO: Remove when we move away from NextJS and SSR.
    const [NetworkTest, OpentokClient] = await Promise.all([
      getNetworkTest(),
      getOpentokClient(),
    ]);

    networkTest.current = new NetworkTest(OpentokClient, sessionData, {
      videoSource,
      audioSource,
      timeout: 30_000,
    });

    setIsConnectionTestRunning(true);
    const timeout = setTimeout(() => {
      if (networkTest.current) {
        logger.warn('TIMEOUT', { sessionId: sessionData.sessionId });
        setConnectionTestError('TIMEOUT');
        stopConnectionTest();
      }
    }, 40_000);

    try {
      const result = await networkTest.current.testQuality();
      if (networkTest.current) {
        logger.info('test quality result', result);
        setConnectionTestResult({
          audioMos: result?.audio?.mos,
          videoQuality: parseVideoQuality(result?.video?.recommendedResolution),
          videoMos: result?.video?.mos,
        });
        setConnectionTestRawResult(result);
      }
    } catch (error) {
      logger.error('UNEXPECTED_ERROR', error);
      setConnectionTestError('UNEXPECTED_ERROR');
    } finally {
      networkTest.current = undefined;
      clearTimeout(timeout);
      setIsConnectionTestRunning(false);
    }
  }, [
    createPreCallTestSession,
    hasAudioAccess,
    hasVideoAccess,
    selectedCameraId,
    selectedMicrophoneId,
    connectionTestError,
    connectionTestResult,
  ]);

  const stopConnectionTest = useCallback(async () => {
    try {
      await networkTest.current?.stop();
    } catch (error) {
      logger.error('UNEXPECTED_ERROR', error);
      setConnectionTestError('UNEXPECTED_ERROR');
    } finally {
      networkTest.current = undefined;
      setIsConnectionTestRunning(false);
    }
  }, [networkTest]);

  const resetConnectionTest = useCallback(() => {
    setConnectionTestResult(undefined);
    setConnectionTestError(undefined);
  }, []);

  // Stop the connection test if media is disabled
  useEffect(() => {
    if (isConnectionTestRunning && (!hasAudioAccess || !hasVideoAccess)) {
      logger.debug('MEDIA_DISABLED');
      setConnectionTestError('MEDIA_DISABLED');

      stopConnectionTest();
    }
  }, [
    isConnectionTestRunning,
    hasAudioAccess,
    hasVideoAccess,
    stopConnectionTest,
  ]);

  // Stop the connection test if the camera or microphone is changed
  useEffect(() => {
    if (!isConnectionTestRunning) {
      return;
    }

    const audioSource = networkTest.current?.options?.audioSource;
    const videoSource = networkTest.current?.options?.videoSource;
    if (
      audioSource !== selectedMicrophoneId ||
      videoSource !== selectedCameraId
    ) {
      logger.debug('MEDIA_CHANGED');
      setConnectionTestError('MEDIA_CHANGED');

      stopConnectionTest();
    }
  }, [
    selectedCameraId,
    selectedMicrophoneId,
    isConnectionTestRunning,
    stopConnectionTest,
  ]);

  // Stop the connection test if the user is on a mobile device and the document is not visible
  useEffect(() => {
    if (isConnectionTestRunning && isMobile && !isVisible) {
      logger.debug('MOBILE_DOCUMENT_NOT_VISIBLE');
      setConnectionTestError('MOBILE_DOCUMENT_NOT_VISIBLE');
      stopConnectionTest();
    }
  }, [isMobile, isVisible, stopConnectionTest, isConnectionTestRunning]);

  useDocumentVisibility(setIsVisible);

  useEffect(() => {
    if (!isCheckedIn) {
      return;
    }

    dispatch(
      updateRealtimeState({
        preCallTest: {
          isRunning: isConnectionTestRunning,
          error: connectionTestError,
          result: connectionTestResult,
        },
      }),
    );
  }, [
    isConnectionTestRunning,
    connectionTestResult,
    connectionTestError,
    dispatch,
    isCheckedIn,
  ]);

  return {
    startConnectionTest,
    stopConnectionTest,
    resetConnectionTest,
    isConnectionTestRunning,
    connectionTestResult,
    connectionTestError,
    connectionTestRawResult,
  };
};

export const useConnectionTest = singletonHook(
  hookInitValues,
  useConnectionTestImpl,
);
